// ═══════════════════════════════════════════════════════════════════════════════
// Browser / PWA chrome (theme-color, iOS status bar) aligned with app light/dark
// ═══════════════════════════════════════════════════════════════════════════════

import { THEME, type Theme } from '@fdh/shared';
import { isPlayStoreClient } from './clientChannel';

/**
 * Matches AppTopBar in dark mode (.dark header.bg-white → gray-900).
 * Keep `index.html` inline script hex values in sync when changing these.
 */
export const BROWSER_CHROME_THEME_COLOR_LIGHT = '#ffffff';
export const BROWSER_CHROME_THEME_COLOR_DARK = '#111827';

const THEME_COLOR_LIGHT_MEDIA = '(prefers-color-scheme: light)';
const THEME_COLOR_DARK_MEDIA = '(prefers-color-scheme: dark)';

const setThemeColorContent = (media: string, content: string): void => {
  const meta = document.querySelector(`meta[name="theme-color"][media="${media}"]`);
  if (meta) {
    meta.setAttribute('content', content);
  }
};

export const syncBrowserChromeTheme = (isDark: boolean, theme: Theme = THEME.SYSTEM): void => {
  const themeMetas = document.querySelectorAll('meta[name="theme-color"]');
  if (themeMetas.length === 0) {
    return;
  }

  if (theme === THEME.LIGHT || theme === THEME.DARK) {
    const color = isDark ? BROWSER_CHROME_THEME_COLOR_DARK : BROWSER_CHROME_THEME_COLOR_LIGHT;
    themeMetas.forEach((meta) => {
      meta.setAttribute('content', color);
    });
  } else {
    const lightMeta = document.querySelector(
      `meta[name="theme-color"][media="${THEME_COLOR_LIGHT_MEDIA}"]`,
    );
    const darkMeta = document.querySelector(
      `meta[name="theme-color"][media="${THEME_COLOR_DARK_MEDIA}"]`,
    );
    if (lightMeta || darkMeta) {
      setThemeColorContent(THEME_COLOR_LIGHT_MEDIA, BROWSER_CHROME_THEME_COLOR_LIGHT);
      setThemeColorContent(THEME_COLOR_DARK_MEDIA, BROWSER_CHROME_THEME_COLOR_DARK);
    } else {
      themeMetas.forEach((meta) => {
        meta.setAttribute(
          'content',
          isDark ? BROWSER_CHROME_THEME_COLOR_DARK : BROWSER_CHROME_THEME_COLOR_LIGHT,
        );
      });
    }
  }

  const appleMeta = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');
  if (appleMeta) {
    appleMeta.setAttribute('content', isDark ? 'black' : 'default');
  }

  document.documentElement.style.colorScheme = isDark ? 'dark' : 'light';
};

/**
 * Chrome TWA paints the Android status bar from launch extras, not `theme-color`.
 * If the bar scheme (`sb=`) or theme mode (`tm=`) does not match the in-app theme,
 * reopen the Play shell so LauncherActivity can apply matching extras.
 * Uses sessionStorage so React Router navigations that drop the query still match.
 */
const TWA_SB_SESSION_KEY = 'fdh-twa-sb';
const TWA_TM_SESSION_KEY = 'fdh-twa-tm';

const readQueryOrSession = (params: URLSearchParams, queryKey: string, sessionKey: string): string | null => {
  const fromQuery = params.get(queryKey);
  if (fromQuery) {
    try {
      sessionStorage.setItem(sessionKey, fromQuery);
    } catch {
      // ignore
    }
    return fromQuery;
  }
  try {
    return sessionStorage.getItem(sessionKey);
  } catch {
    return null;
  }
};

let playTwaRelaunchStarted = false;

export const syncPlayTwaStatusBar = (isDark: boolean, theme: Theme = THEME.SYSTEM): void => {
  if (typeof window === 'undefined') {
    return;
  }
  if (!isPlayStoreClient()) {
    return;
  }
  if (playTwaRelaunchStarted) {
    return;
  }
  const params = new URLSearchParams(window.location.search);
  const statusBarScheme = readQueryOrSession(params, 'sb', TWA_SB_SESSION_KEY);
  const themeMode = readQueryOrSession(params, 'tm', TWA_TM_SESSION_KEY);
  const wantedScheme = isDark ? 'dark' : 'light';
  if (statusBarScheme === wantedScheme && themeMode === theme) {
    return;
  }
  playTwaRelaunchStarted = true;
  window.location.assign(
    `maideasy://status-bar?dark=${isDark ? '1' : '0'}&mode=${encodeURIComponent(theme)}`,
  );
};
